import { useNavigate } from 'react-router'
import type { ColumnDef } from '@tanstack/react-table'
import type { CatalogItem } from '@hangarmax/types'
import { useAdminCatalogList, useCatalogProvenance, useDeleteCatalogItem } from '@/hooks/useAdminCatalog'
import { DataTable } from '@/components/shared/DataTable'
import { DataTableColumnHeader } from '@/components/shared/DataTableColumnHeader'
import { DataTableFacetedFilter } from '@/components/shared/DataTableFacetedFilter'
import { PageHeader } from '@/components/shared/PageHeader'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { useUpdateCatalogItem } from '@/hooks/useAdminCatalog'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Plus, MoreHorizontal, Pencil, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Input } from '@/components/ui/input'
import { Search } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { fetchCatalogUsage } from '@/lib/api/admin-stats'
import { CLASS_TIER, TIER_LABELS } from '@/lib/tierLadder'
import { AIRCRAFT_CLASSES, AIRCRAFT_CLASS_LABEL } from '@/lib/sceneLabels'
import { useAdminAuth } from '@/contexts/AdminAuthContext'
import {
  AGREEMENT_LABEL, DRIFT_THRESHOLD_PCT, PRIMARY_LABEL, SOURCE_LABEL, provenanceBucket,
  type CatalogProvenance, type ProvenanceBucket,
} from '@/lib/dimensionProvenance'

const categoryColors: Record<string, string> = {
  aircraft: 'bg-blue-100 text-blue-800',
  vehicle: 'bg-green-100 text-green-800',
  equipment: 'bg-orange-100 text-orange-800',
  furniture: 'bg-amber-100 text-amber-800',
  structural: 'bg-gray-100 text-gray-800',
  custom: 'bg-pink-100 text-pink-800',
}

// Same palette as the tier badges on the Users page.
const planColors: Record<string, string> = {
  pilot: 'bg-blue-100 text-blue-800',
  pro: 'bg-purple-100 text-purple-800',
  business: 'bg-green-100 text-green-800',
}

const agreementColors: Record<ProvenanceBucket['agreement'], string> = {
  verified: 'bg-green-100 text-green-800',
  single_source: 'bg-sky-100 text-sky-800',
  conflict: 'bg-red-100 text-red-800',
  unverified: 'bg-amber-100 text-amber-800',
  no_research: 'bg-gray-100 text-gray-700',
}

const toOptions = (labels: Record<string, string>) =>
  Object.entries(labels).map(([value, label]) => ({ value, label }))

const inFilter = (row: { getValue: (id: string) => unknown }, id: string, value: string[]) =>
  value.includes(row.getValue(id) as string)

const AIRCRAFT_CLASS_FILTER_OPTIONS = [
  ...AIRCRAFT_CLASSES.map((c) => ({ value: c, label: `${AIRCRAFT_CLASS_LABEL[c]} · ${TIER_LABELS[CLASS_TIER[c]]}` })),
  { value: 'untagged', label: 'Untagged' },
]

export function CatalogListPage() {
  const navigate = useNavigate()
  const { data: items, isLoading } = useAdminCatalogList()
  const { data: provenance } = useCatalogProvenance()
  const { adminRole } = useAdminAuth()
  // is_admin() (the aircraft_research RLS check) accepts admin/super_admin only.
  const researchHidden = adminRole === 'support'
  const { data: usage } = useQuery({
    queryKey: ['admin-catalog-usage'],
    queryFn: fetchCatalogUsage,
    staleTime: 60 * 60 * 1000, // 1 hour — expensive aggregation
  })
  const updateItem = useUpdateCatalogItem()
  const deleteItem = useDeleteCatalogItem()
  const [globalFilter, setGlobalFilter] = useState('')

  const provenanceMap = new Map<string, { row: CatalogProvenance; bucket: ProvenanceBucket }>(
    (provenance ?? []).map((p) => [p.catalog_id, { row: p, bucket: provenanceBucket(p) }]),
  )
  // TanStack caches every row's accessor values until `data` changes identity.
  // Provenance arrives after the catalog, so without a fresh array the three
  // dim_* columns keep the null they read first: facets show no counts and every
  // provenance filter returns "No catalog items" (seen on prod 2026-09-30).
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `provenance` is the point: it must bust the row cache
  const tableData = useMemo(() => [...(items ?? [])], [items, provenance])
  const placementMap = new Map((usage?.rows ?? []).map((r) => [r.catalog_id, r.placements]))
  const top10 = (usage?.rows ?? []).slice(0, 10).map((r) => ({
    name: r.display_name.length > 18 ? r.display_name.slice(0, 16) + '…' : r.display_name,
    placements: r.placements,
  }))

  const columns: ColumnDef<CatalogItem, unknown>[] = [
    {
      accessorKey: 'display_name',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          {row.original.thumbnail_url ? (
            <img src={row.original.thumbnail_url} className="h-8 w-8 rounded object-cover" alt="" />
          ) : (
            <div className="h-8 w-8 rounded bg-muted" />
          )}
          <div>
            <p className="font-medium">{row.original.display_name}</p>
            <p className="text-xs text-muted-foreground">{row.original.asset_code}</p>
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'category',
      header: 'Category',
      cell: ({ getValue }) => {
        const cat = getValue() as string
        return <Badge variant="secondary" className={categoryColors[cat]}>{cat}</Badge>
      },
      filterFn: 'equals',
    },
    {
      id: 'aircraft_class',
      header: 'Class',
      // Aircraft rows only. `untagged` is a real bucket: the app falls back to is_premium for it.
      accessorFn: (row) => (row.category === 'aircraft' ? row.aircraft_class ?? 'untagged' : null),
      filterFn: (row, id, value: string[]) => value.includes(row.getValue(id) as string),
      cell: ({ row }) => {
        if (row.original.category !== 'aircraft') return <span className="text-muted-foreground">—</span>
        const cls = row.original.aircraft_class
        if (!cls || !(cls in CLASS_TIER)) {
          return <span className="text-xs text-muted-foreground">{cls ?? 'untagged'}</span>
        }
        const plan = CLASS_TIER[cls]
        return (
          <Badge variant="secondary" className={planColors[plan]} title={`Placeable from ${TIER_LABELS[plan]}`}>
            {AIRCRAFT_CLASS_LABEL[cls]}
          </Badge>
        )
      },
    },
    {
      accessorKey: 'subcategory',
      header: 'Subcategory',
      cell: ({ getValue }) => (
        <span className="text-sm text-muted-foreground">{(getValue() as string) ?? '—'}</span>
      ),
    },
    {
      id: 'dimensions',
      header: 'Dimensions (m)',
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.width_m} × {row.original.length_m} × {row.original.height_m}
        </span>
      ),
    },
    {
      id: 'dim_source',
      header: 'Dim. source',
      accessorFn: (row) => provenanceMap.get(row.id)?.bucket.source ?? null,
      filterFn: inFilter,
      // bucket keys ('single_source', 'shared', …) would substring-match the search box
      enableGlobalFilter: false,
      cell: ({ row }) => {
        const p = provenanceMap.get(row.original.id)
        if (!p) return <span className="text-muted-foreground">—</span>
        const { row: r, bucket: b } = p
        const tooltip = [
          r.research_width_m != null
            ? `Research: ${r.research_width_m} × ${r.research_length_m} × ${r.research_height_m} m ` +
              `(span ${r.width_drift_pct ?? '—'} %, length ${r.length_drift_pct ?? '—'} % off)`
            : 'No matched research row',
          `Sources: ${(r.source_types ?? []).join(', ') || '—'}`,
          ...(r.candidate_count > 1 ? [`${r.candidate_count} research candidates — nearest shown`] : []),
          'FAA-ACD never covers height.',
        ].join('\n')
        return (
          <div className="flex flex-wrap items-center gap-1" title={tooltip}>
            <Badge variant="outline" className="text-xs">
              {b.source === 'proxy' && row.original.sub_name ? `Proxy · ${row.original.sub_name}` : SOURCE_LABEL[b.source]}
            </Badge>
            {researchHidden ? (
              <span className="text-xs text-muted-foreground">research: admin only</span>
            ) : (
              <>
                <Badge variant="secondary" className={agreementColors[b.agreement]}>{AGREEMENT_LABEL[b.agreement]}</Badge>
                {r.faa_acd_agrees && (
                  <Badge variant="secondary" className="bg-indigo-100 text-indigo-800"
                    title={r.faa_acd_both_axes ? 'Span and length within 2 %' : 'FAA-ACD covers one axis only — that axis within 2 %'}>
                    FAA-ACD ✓{r.faa_acd_both_axes ? '' : ' (1 axis)'}
                  </Badge>
                )}
                {r.primary_agrees && <Badge variant="secondary" className="bg-violet-100 text-violet-800">MFR/TCDS ✓</Badge>}
                {b.drifted && (
                  <span className="text-xs text-amber-700">⚠ &gt;{DRIFT_THRESHOLD_PCT} % off research</span>
                )}
              </>
            )}
          </div>
        )
      },
    },
    {
      id: 'dim_agreement',
      accessorFn: (row) => provenanceMap.get(row.id)?.bucket.agreement ?? null,
      filterFn: inFilter,
      enableGlobalFilter: false,
    },
    {
      id: 'dim_primary',
      accessorFn: (row) => provenanceMap.get(row.id)?.bucket.primary ?? null,
      filterFn: inFilter,
      enableGlobalFilter: false,
    },
    {
      accessorKey: 'model_type',
      header: 'Model',
      cell: ({ getValue }) => (
        <Badge variant="outline">{getValue() as string}</Badge>
      ),
    },
    {
      accessorKey: 'is_active',
      header: 'Active',
      cell: ({ row }) => (
        <Switch
          checked={row.original.is_active}
          onCheckedChange={(checked) => {
            updateItem.mutate({ id: row.original.id, updates: { is_active: checked } })
          }}
        />
      ),
    },
    {
      accessorKey: 'is_premium',
      header: 'Premium',
      cell: ({ row }) => (
        <Switch
          checked={row.original.is_premium}
          onCheckedChange={(checked) => {
            updateItem.mutate({ id: row.original.id, updates: { is_premium: checked } })
          }}
        />
      ),
    },
    {
      accessorKey: 'sort_order',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Sort" />,
    },
    {
      id: 'placements',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Placements" />,
      accessorFn: (row) => placementMap.get(row.id) ?? 0,
      cell: ({ row }) => {
        const n = placementMap.get(row.original.id) ?? 0
        return n > 0 ? (
          <span className="font-medium">{n.toLocaleString()}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )
      },
      sortingFn: (a, b) =>
        (placementMap.get(a.original.id) ?? 0) - (placementMap.get(b.original.id) ?? 0),
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => navigate(`/catalog/${row.original.id}`)}>
              <Pencil className="mr-2 h-4 w-4" /> Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => {
                if (confirm(`Delete "${row.original.display_name}"?`)) {
                  deleteItem.mutate(row.original.id)
                }
              }}
            >
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        title="Catalog"
        description={`${items?.length ?? 0} items in the asset catalog`}
        actions={
          <Button onClick={() => navigate('/catalog/new')}>
            <Plus className="mr-2 h-4 w-4" /> New Item
          </Button>
        }
      />

      {top10.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Most-placed items</CardTitle>
            <CardDescription>
              Top 10 by total placements across {usage?.total_layouts_scanned ?? 0} layouts
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={top10} margin={{ left: 10, right: 10, top: 4, bottom: 4 }}>
                <XAxis dataKey="name" angle={-25} textAnchor="end" interval={0} height={64} fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip />
                <Bar dataKey="placements" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <DataTable
        columns={columns}
        data={tableData}
        isLoading={isLoading}
        globalFilter={globalFilter}
        onGlobalFilterChange={setGlobalFilter}
        emptyMessage="No catalog items."
        hiddenColumns={['dim_agreement', 'dim_primary']}
        toolbar={(table) => (
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative max-w-sm flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search catalog..."
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="pl-9"
              />
            </div>
            <DataTableFacetedFilter
              column={table.getColumn('aircraft_class')}
              title="Aircraft class"
              options={AIRCRAFT_CLASS_FILTER_OPTIONS}
            />
            <DataTableFacetedFilter
              column={table.getColumn('dim_source')}
              title="Dim. source"
              options={toOptions(SOURCE_LABEL)}
            />
            {!researchHidden && (
              <>
                <DataTableFacetedFilter
                  column={table.getColumn('dim_agreement')}
                  title="Research agreement"
                  options={toOptions(AGREEMENT_LABEL)}
                />
                <DataTableFacetedFilter
                  column={table.getColumn('dim_primary')}
                  title="Primary check"
                  options={toOptions(PRIMARY_LABEL)}
                />
              </>
            )}
          </div>
        )}
      />
    </div>
  )
}
